import { type RefObject, useLayoutEffect, useRef, useState } from "react";
import { measureRoom, type Room, type RoomSpec } from "./drop-room.js";
import type { ArtVariant } from "./palette.js";
import type { Outline } from "./picture.js";
import { PixelArt } from "./pixel-art.js";
import "./pixel-drop.css";
import type { SceneName } from "./scenes.js";
import { useTurningPalette } from "./turning.js";

/** The shapes a drop takes (DESIGN.md §9, "Pixel art", "Drops"). */
export type DropShape = "corner" | "horizon" | "rise" | "edge";

/** Where each shape's light sits and how far it reaches, in shares of its room. */
const OUTLINES: Record<DropShape, Outline> = {
  corner: { x: 1, y: 0, rx: 0.98, ry: 0.98 },
  horizon: { x: 0.5, y: 0, rx: 0.49, ry: 0.98 },
  rise: { x: 0.5, y: 1, rx: 0.49, ry: 0.98 },
  edge: { x: 1, y: 0.5, rx: 0.98, ry: 0.49 },
};

const SCENES: Record<DropShape, SceneName> = {
  corner: "bloom",
  horizon: "drift",
  rise: "calm",
  edge: "bloom",
};

const COLOURS = {
  turning: ["sky", "dawn"],
  sky: ["sky"],
  dawn: ["dawn"],
} as const satisfies Record<string, readonly ArtVariant[]>;

const same = (a: Room | null, b: Room | null) =>
  a !== null &&
  b !== null &&
  a.left === b.left &&
  a.top === b.top &&
  a.width === b.width &&
  a.height === b.height &&
  a.round === b.round;

/**
 * Measured off the surface as it is set, and again when it resizes, its words
 * change, the window resizes or the faces arrive.
 */
function useRoom(self: RefObject<HTMLDivElement | null>, spec: RoomSpec): Room | null {
  const [room, setRoom] = useState<Room | null>(null);
  const { edge, keep, clear, until, down } = spec;
  useLayoutEffect(() => {
    const host = self.current?.parentElement ?? null;
    // A document that lays nothing out (a test's) has no room to measure, so
    // no art rather than art placed by guess.
    if (host === null || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const next = measureRoom(host, { edge, keep, clear, until, down });
      setRoom((was) => (same(was, next) || (was === null && next === null) ? was : next));
    };
    measure();
    const sizes = new ResizeObserver(measure);
    sizes.observe(host);
    const words = new MutationObserver(measure);
    words.observe(host, { subtree: true, childList: true, characterData: true });
    window.addEventListener("resize", measure);
    if ("fonts" in document) void document.fonts.ready.then(measure);
    return () => {
      sizes.disconnect();
      words.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [self, edge, keep, clear, until, down]);
  return room;
}

/**
 * A drop of pixel art in the empty room of a surface (DESIGN.md §9, "Pixel
 * art", "Drops"). Its parent is the surface; under the words it is never
 * behind one, and where there is no room it is not drawn.
 */
export function PixelDrop({
  shape,
  clear,
  until,
  down,
  colour = "sky",
  cell = 6,
}: {
  readonly shape: DropShape;
  /** Selectors for the words the room starts right of. */
  readonly clear?: string;
  /** Selectors for the controls the room ends left of. */
  readonly until?: string;
  /** The element whose foot the room ends at: the surface's foot without one. */
  readonly down?: string;
  /** Sky and dawn in turn for a surface that stays; one, still, for one that comes and goes. */
  readonly colour?: keyof typeof COLOURS;
  readonly cell?: number;
}) {
  const self = useRef<HTMLDivElement>(null);
  const room = useRoom(self, {
    edge: shape === "edge",
    keep: shape === "corner" ? "end" : "middle",
    clear,
    until,
    down,
  });
  const variant = useTurningPalette(COLOURS[colour]);
  return (
    <div
      ref={self}
      className="pixel-drop"
      style={
        room === null
          ? { width: 0, height: 0 }
          : {
              left: room.left,
              top: room.top,
              width: room.width,
              height: room.height,
              borderRadius: room.round,
            }
      }
    >
      {room === null ? null : (
        <PixelArt
          variant={variant}
          scene={SCENES[shape]}
          cell={cell}
          outline={OUTLINES[shape]}
          interactive
          className="pixel-drop-art"
        />
      )}
    </div>
  );
}
